import { useEffect, useRef, useState } from "react";
import { Divider } from "@mui/material";
import dayjs from "dayjs";
import { receiveOrdersParam } from "features/stock-ordering/core/stock-ordering.params";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  ActionHeader,
  BannerIndicator,
  CancelAndConfirm,
  isQuantityEmpty,
  useActionAvailability,
} from "./stock-ordering-utils";
import {
  openMessageModal,
  closeMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { updateReceiveOrders } from "../slices/update-receive-order.slice";
import { MaterialDateTimeInput } from "features/shared/presentation/components";
import { useParams } from "react-router-dom";
import { selectGetProductData } from "../slices/get-product-data.slice";
import { selectTableDataChange } from "../slices/tableDataChange.slice";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { CheckCircle } from "lucide-react";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

interface StockOrderProcessStoreRecieveOrderProps {
  onClose: (close: boolean) => void;
}

interface ActualDeliveryReceieveOrder {
  disableConfirmButton: boolean;
  handleActionReceievehButtonClicked: (date: string) => void;
}
export function StockOrderProcessStoreRecieveOrder(
  props: StockOrderProcessStoreRecieveOrderProps
) {
  /**
   * url param that contains order id
   */
  const { orderId } = useParams();

  /**
   * dispatch and selector user for sending and fetching request
   */
  const dispatch = useAppDispatch();
  const tableDataChangeState = useAppSelector(selectTableDataChange);
  const getProductDataState = useAppSelector(selectGetProductData);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const { data, status } = tableDataChangeState;
  const { order_information, product_batch_data } =
    getProductDataState.data ?? {};

  /**
   * Hooks for availability
   */
  const { processId, actionAvailable, permission } = useActionAvailability(1);
  const supplierAccess = useActionAvailability(5).permission;

  /**
   * Message modal for order confirmation
   * @param actualDeliveryDate - actual delivery date
   */
  const handleReceiveButton = (actualDeliveryDate?: string) => {
    dispatch(
      openMessageModal({
        useHtml: true,
        message: `<b>Please review the items received. Do the quantities match your order, or are there any discrepancies?</b>
                  <br><br>
                  By clicking <b>I CONFIRM</b>, you acknowledge that the order was received on 
                  <b>${dayjs(actualDeliveryDate).format(
                    "llll"
                  )}</b>. This confirmation serves as proof of delivery and acceptance of the items. 
                  <br><br>
                  By confirming, you also agree to our 
                  <a href="terms-and-condition" target="_blank"><b>Terms & Conditions</b></a>.
                  `,
        buttons: [
          {
            color: "#22201A",
            text: "No",
            onClick: () => {
              dispatch(closeMessageModal());
            },
          },
          {
            color: "#CC5801",
            text: "I confirm",
            onClick: () => {
              if (orderId && data?.productData.length !== 0) {
                const multimOrdering =
                  order_information?.companyId === 1
                    ? undefined
                    : isQuantityEmpty(data?.productData ?? [], processId);

                const receiveOrdersParamData: receiveOrdersParam = {
                  id: orderId,
                  data: {
                    withDiscrepancy: multimOrdering,
                    actualDeliveryDate: actualDeliveryDate ?? undefined,
                    productData:
                      data?.productData.map((product) => ({
                        id: product.id,
                        product_id: product.product_id,
                        order_qty: product.delivered_qty,
                        delivered_qty: product.delivered_qty,
                        receive_subtotal: product.receive_subtotal,
                        total_cost: product.total_cost,
                      })) ?? [],
                  },
                };

                dispatch(updateReceiveOrders(receiveOrdersParamData));

                if (
                  data?.productData &&
                  isQuantityEmpty(data?.productData, processId)
                ) {
                  const link = `${REACT_APP_DOMAIN_URL}api/stock/delivery-adjustment/${orderId}/delivery-adjustment`;
                  window.open(link, "_blank");
                }

                props.onClose(true);
              }
              dispatch(closeMessageModal());
            },
          },
        ],
      })
    );
  };

  if (order_information?.companyId === 1 && product_batch_data?.length !== 0) {
    return (
      <>
        <BannerIndicator
          visible={actionAvailable}
          content="This product has arrived at your store. Please click Receive to acknowledge and record the delivery."
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />

        <BannerIndicator
          visible={!actionAvailable}
          content="The product has been delivered and is awaiting confirmation from the store."
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />
      </>
    );
  }

  if (data?.productData) {
    return (
      <>
        <ActionHeader
          primary={"Receive Order"}
          secondary="Confirm that the products have arrived by entering the actual delivery date and the quantities received from the supplier."
          HeaderIcon={CheckCircle}
        />

        {actionAvailable ? (
          <>
            <BannerIndicator
              visible={
                !permission &&
                order_information?.deliveryReceiveManagerRejectDate !== null
              }
              content="The order has been rejected by store manager and is awaiting store confirmation to verify whether the delivered items are complete, accurate, and in proper condition before proceeding to the next step."
              textColor="text-[#4A4A4A]"
              bgColor="bg-[#AEE1F7]"
            />

            <ActualDeliveryReceieveOrder
              disableConfirmButton={!status}
              handleActionReceievehButtonClicked={handleReceiveButton}
            />
          </>
        ) : (
          <BannerIndicator
            visible={!permission}
            content="Awaiting confirmation to verify whether the delivered items are complete, accurate, and in proper condition before proceeding to the next step."
            textColor="text-[#4A4A4A]"
            bgColor="bg-[#AEE1F7]"
          />
        )}
      </>
    );
  }

  return null;
}

/**
 * @author knhrvs - kenoresca
 *
 * Actual Delivery Date Setup and Receive button
 * -----------------------------------------------------
 *
 * @param handleActionDispatchButtonClicked - handles on dispatch button click
 * @param disableConfirmButton - disables dispatch button
 * @returns React Element - route and dispatch button component
 */

const ActualDeliveryReceieveOrder = (props: ActualDeliveryReceieveOrder) => {
  /**
   * Destructure props
   */
  const { handleActionReceievehButtonClicked, disableConfirmButton } = props;

  /**
   *  @constant actualDeliveryDate - state for actual delivery date. The default value is the current date
   */
  const [actualDeliveryDate, setActualDeliveryDate] = useState(
    dayjs().format("YYYY-MM-DD HH:mm:ss")
  );

  /**
   *  @constant openCalendar - state to trigger opening of calendar
   */
  const [openCalendar, setOpenCalendar] = useState(false);

  /**
   * handles the change of actual delivery date
   * @param date - date
   */
  const handleActualDeliveryChange = (
    date: string | number | Date | dayjs.Dayjs | null | undefined
  ) => {
    if (date) {
      const formattedDate = dayjs(date).format("YYYY-MM-DD HH:mm:ss");

      setActualDeliveryDate(formattedDate);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:justify-between w-full font-[Roboto] space-y-3 px-1">
      <div className="flex flex-col">
        <div className="flex space-x-2">
          <p className="font-semibold">Set actual delivery date:</p>
          <button
            onClick={() =>
              setActualDeliveryDate(dayjs().format("YYYY-MM-DD HH:mm:ss"))
            }
            className="flex text-xs self-center"
          >
            [ <p className="text-button">Reset</p>]
          </button>
        </div>

        <MaterialDateTimeInput
          colorTheme={"black"}
          view={["month", "day", "hours", "minutes"]}
          fullWidth
          size="small"
          readonly
          iconColor="gray"
          value={actualDeliveryDate}
          textFieldValue={actualDeliveryDate}
          openCalendar={openCalendar}
          setOpenCalendar={(val) => setOpenCalendar(val)}
          onChange={handleActualDeliveryChange}
          minDateTime={dayjs().subtract(6, "months")}
          placeholder="Actual Delivery Date"
        />
      </div>

      <CancelAndConfirm
        disableConfirm={disableConfirmButton}
        confirmButtonName="Receive Order"
        handleConfirm={() =>
          handleActionReceievehButtonClicked(actualDeliveryDate)
        }
        visible
      />
    </div>
  );
};
